export type UserFacingError = {
  title: string;
  description: string;
  actionLabel?: string;
  actionHref?: string;
};

type ErrorLike = {
  name?: string;
  message?: string;
  info?: { errorCode?: string | number; errorMessage?: string; data?: unknown };
  response?: {
    status?: number;
    data?: {
      code?: string | number;
      message?: string | string[];
      errorMessage?: string;
      error?: string;
      msg?: string;
    };
  };
  request?: unknown;
};

const payloadMessage = (error: ErrorLike): string => {
  const payload = error.response?.data;
  const value =
    payload?.message ??
    payload?.errorMessage ??
    payload?.msg ??
    payload?.error ??
    error.info?.errorMessage ??
    error.message;
  return Array.isArray(value) ? value.join('；') : value || '';
};

const payloadCode = (error: ErrorLike): string =>
  String(error.response?.data?.code ?? error.info?.errorCode ?? '');

const conflictMessage = (technicalMessage: string): UserFacingError => {
  const message = technicalMessage.toLowerCase();
  if (
    message.includes('already occupied') ||
    message.includes('space point occupied')
  ) {
    return {
      title: '这个空间点位已经有绿植了',
      description: '一个空间点位只能放置一株在场绿植，请选择其他未占用点位。',
    };
  }
  if (
    message.includes('code already exists') ||
    message.includes('name already exists')
  ) {
    return {
      title: '编码或名称已经被使用',
      description: '请换一个未使用的编码或名称后重新保存。',
    };
  }
  if (message.includes('inactive')) {
    return {
      title: '所选点位当前已停用',
      description: '请先在“空间点位”中启用该点位，或者选择其他启用中的点位。',
      actionLabel: '查看空间点位',
      actionHref: '/greenplant/space-points',
    };
  }
  return {
    title: '当前状态暂时不能执行这个操作',
    description: '数据可能刚被其他人处理过，请刷新页面确认最新状态后再试。',
  };
};

export const resolveUserFacingError = (value: unknown): UserFacingError => {
  const error = (value || {}) as ErrorLike;
  const status = error.response?.status;
  const code = payloadCode(error);
  const technicalMessage = payloadMessage(error);

  if (code === 'DATA_SCOPE_FORBIDDEN') {
    return {
      title: '当前账号还没有这部分数据的访问范围',
      description:
        '请联系企业管理员，在“企业管理 → 权限资源”中授予“数据范围授权”权限，并由获授权管理员为你配置对应的区域、空间点位或供应商范围。',
    };
  }
  if (code === 'FORBIDDEN' || status === 403) {
    return {
      title: '当前账号没有执行此操作的权限',
      description:
        '请联系企业管理员，在“企业管理 → 应用权限”中为你的角色配置相应的绿植功能权限。',
    };
  }
  if (code === 'IDENTITY_RESOURCES_NOT_IMPORTED') {
    return {
      title: '绿植应用权限还没有完成初始化',
      description:
        '请企业管理员先在企业管理中导入绿植应用权限资源，再初始化默认角色。',
    };
  }
  if (code === 'DEPENDENCY_UNAVAILABLE' || status === 503) {
    return {
      title: '平台服务暂时不可用',
      description:
        '人员、组织或权限服务暂时没有响应，请稍后重试；持续失败请联系系统管理员。',
    };
  }
  if (code === 'SPACE_POINT_OCCUPIED') {
    return conflictMessage('space point occupied');
  }
  if (code === 'DUPLICATE_TASK') {
    return {
      title: '相同的养护任务已经存在',
      description: '无需重复创建，请在养护任务列表中查看已有任务。',
      actionLabel: '查看养护任务',
      actionHref: '/greenplant/tasks',
    };
  }
  if (code === 'CHANGE_RECORD_REQUIRED') {
    return {
      title: '绿植位置不能直接修改',
      description:
        '请通过异常关闭或正式变更记录完成调拨、更换或移除，以保留完整历史。',
    };
  }
  if (code === 'ATTACHMENT_STORAGE_NOT_CONFIGURED') {
    return {
      title: '图片存储尚未配置',
      description:
        '请系统管理员配置附件存储目录后重启服务。其他不含附件的操作仍可继续。',
    };
  }
  if (code === 'ATTACHMENT_TEMP_QUOTA_EXCEEDED') {
    return {
      title: '待提交图片过多',
      description: '请删除不需要的待上传图片，或等待之前的临时图片清理后再试。',
    };
  }
  if (code === 'ATTACHMENT_UPLOAD_BUSY') {
    return {
      title: '图片上传人数较多',
      description: '请稍等片刻后重新上传，已经保存的业务数据不会丢失。',
    };
  }
  if (code === 'DATA_SCOPE_SUBJECT_NOT_FOUND') {
    return {
      title: '没有找到所选人员或组织',
      description: '该人员或组织可能已被移除，请重新选择后保存授权。',
    };
  }
  if (code === 'DATA_SCOPE_KEY_NOT_FOUND') {
    return {
      title: '没有找到所选授权范围',
      description: '该区域、点位或供应商可能已被删除，请重新选择范围。',
    };
  }
  if (
    code === 'VALIDATION_ERROR' ||
    code === 'VALIDATION_FAILED' ||
    status === 400
  ) {
    return {
      title: '提交内容还不完整',
      description:
        '请检查页面中的必填项、日期和输入格式，修正标红内容后重新提交。',
    };
  }
  if (code === 'STATE_CONFLICT' || status === 409) {
    return conflictMessage(technicalMessage);
  }
  if (code === 'NOT_FOUND' || status === 404) {
    return {
      title: '这条数据已经不存在或无法访问',
      description:
        '数据可能已被删除，或已不在你的访问范围内，请返回列表重新选择。',
      actionLabel: '返回工作台',
      actionHref: '/greenplant/dashboard',
    };
  }
  if (
    status === 401 &&
    technicalMessage
      .toLowerCase()
      .includes('secret or public key must be provided')
  ) {
    return {
      title: '文件服务鉴权配置异常',
      description:
        '附件服务未正确配置 M2M 令牌验签公钥，当前登录状态正常。请由文件管理服务管理员修复验签配置后重试。',
    };
  }
  if (status === 401) {
    return {
      title: '登录状态已过期',
      description: '系统正在重新登录，完成后会返回当前页面。',
    };
  }
  if (error.request && !error.response) {
    return {
      title: '暂时无法连接到服务',
      description:
        '请检查网络连接后重试；如果其他页面也无法打开，请联系系统管理员。',
    };
  }
  if (status && status >= 500) {
    return {
      title: '系统暂时没有处理成功',
      description:
        '你的操作没有完成，请稍后重试；持续失败请将发生时间告知系统管理员。',
    };
  }
  return {
    title: '操作没有完成',
    description: '请检查填写内容并重试；持续失败请联系系统管理员。',
  };
};

export const userFacingErrorText = (error: unknown): string => {
  const resolved = resolveUserFacingError(error);
  return `${resolved.title}：${resolved.description}`;
};
